import type { MenuProps } from "antd";

import { isValidElement } from "react";
import { matchPath, useLocation } from "react-router";

type MenuItem = Required<MenuProps>["items"][number];
type AutoExpandMenuResult = {
  menuKey?: string;
  selectedKeys?: string[];
  defaultOpenKeys?: string[];
};

const normalizePath = (value?: string) =>
  value && value !== "/" ? value.replace(/\/+$/, "") : value;

const getItemPath = (item: MenuItem): string | undefined => {
  if (!item || typeof item !== "object") return;

  const label = (item as { label?: unknown }).label;

  if (!isValidElement(label)) return;

  const labelProps = (label.props ?? {}) as {
    to?: unknown;
    href?: unknown;
  };

  const to = labelProps.to;
  if (typeof to === "string") return normalizePath(to);

  const href = labelProps.href;
  if (typeof href === "string") return normalizePath(href);

  return;
};

const isMatchingPath = (itemPath: string | undefined, pathname: string) => {
  if (!itemPath) return false;

  const normalizedItemPath = normalizePath(itemPath);
  const normalizedPathname = normalizePath(pathname);

  if (!normalizedItemPath || !normalizedPathname) return false;

  if (normalizedItemPath.includes(":")) {
    return Boolean(
      matchPath({ path: normalizedItemPath, end: false }, normalizedPathname),
    );
  }

  return (
    normalizedPathname === normalizedItemPath ||
    normalizedPathname.startsWith(`${normalizedItemPath}/`)
  );
};

const findMenuSelection = (
  items: MenuProps["items"] | undefined,
  pathname: string,
  parents: string[] = [],
): { key: string; openKeys: string[]; score: number } | undefined => {
  if (!items) return;

  let bestMatch: { key: string; openKeys: string[]; score: number } | undefined;
  for (const item of items) {
    if (!item || typeof item !== "object") continue;

    const key = String(item.key);
    const itemPath = getItemPath(item);

    if (isMatchingPath(itemPath, pathname)) {
      const candidate = {
        key,
        openKeys: parents,
        score: normalizePath(itemPath)?.length ?? 0,
      };

      if (!bestMatch || candidate.score > bestMatch.score) {
        bestMatch = candidate;
      }
    }

    const children = (item as { children?: MenuProps["items"] }).children;
    const childMatch = findMenuSelection(children, pathname, [...parents, key]);
    if (childMatch && (!bestMatch || childMatch.score > bestMatch.score)) {
      bestMatch = childMatch;
    }
  }

  return bestMatch;
};

export const useAutoExpandMenu = (
  items?: MenuProps["items"],
): AutoExpandMenuResult => {
  const { pathname } = useLocation();

  if (!normalizePath(pathname)) return {};

  const matched = findMenuSelection(items, pathname);
  if (matched) {
    return {
      menuKey: pathname,
      selectedKeys: [matched.key],
      defaultOpenKeys: matched.openKeys,
    };
  }

  if (items?.length) {
    return {
      menuKey: pathname,
      selectedKeys: [],
      defaultOpenKeys: [],
    };
  }

  const [root, ...rest] = pathname.split("/").filter(Boolean);
  const leaf = rest[rest.length - 1];

  if (!root) return {};

  return {
    menuKey: pathname,
    selectedKeys: [leaf ?? root],
    ...(leaf && { defaultOpenKeys: [root] }),
  };
};
